/**
 * ElementConfigModal Component
 *
 * Modal for configuring individual flow elements.
 * Different forms per element type.
 *
 * @component
 */

import React, { useState, useEffect } from 'react';
import {
  Monitor,
  GitBranch,
  Equal,
  RefreshCw,
  Download,
  PlusCircle,
  Edit2,
  Trash2,
  Mail,
  MessageSquare,
  Clock,
  Zap,
  Database,
  Plus,
  X
} from 'lucide-react';
import {
  StandardModal,
  StandardButton,
  StandardInput,
  StandardSelect,
  StandardTextarea,
  StandardCheckbox,
  StandardCard,
  useToast
} from '../../standard';
import type {
  FlowElement,
  ElementType,
  ScreenConfig,
  ScreenField,
  DecisionConfig,
  DecisionOutcome,
  AssignmentConfig,
  AssignmentItem,
  LoopConfig,
  GetRecordsConfig,
  CreateRecordsConfig,
  UpdateRecordsConfig,
  DeleteRecordsConfig,
  EmailAlertConfig,
  WaitConfig,
  FlowCondition,
  FlowVariable
} from '../../../types/flowAutomation';

// ============================================
// INTERFACES
// ============================================

interface ElementConfigModalProps {
  isOpen: boolean;
  onClose: () => void;
  element: FlowElement | null;
  variables: FlowVariable[];
  onSave: (element: FlowElement) => void;
}

// ============================================
// CONSTANTS
// ============================================

const OBJECT_TYPES = [
  { value: 'Client', label: 'Client' },
  { value: 'Contact', label: 'Contact' },
  { value: 'Task', label: 'Task' },
  { value: 'Opportunity', label: 'Opportunity' },
  { value: 'Case', label: 'Case' },
  { value: 'Lead', label: 'Lead' },
  { value: 'Account', label: 'Account' },
  { value: 'CreditReport', label: 'Credit Report' },
  { value: 'CyberSweepClient', label: 'CyberSweep Client' }
];

const FIELD_TYPES = [
  { value: 'text', label: 'Text' },
  { value: 'number', label: 'Number' },
  { value: 'date', label: 'Date' },
  { value: 'email', label: 'Email' },
  { value: 'phone', label: 'Phone' },
  { value: 'textarea', label: 'Long Text' },
  { value: 'checkbox', label: 'Checkbox' },
  { value: 'picklist', label: 'Picklist' },
  { value: 'currency', label: 'Currency' }
];

const OPERATORS = [
  { value: 'equals', label: 'Equals' },
  { value: 'not_equals', label: 'Does Not Equal' },
  { value: 'greater_than', label: 'Greater Than' },
  { value: 'less_than', label: 'Less Than' },
  { value: 'contains', label: 'Contains' },
  { value: 'not_contains', label: 'Does Not Contain' },
  { value: 'is_null', label: 'Is Null' },
  { value: 'is_not_null', label: 'Is Not Null' }
];

// ============================================
// ELEMENT ICON HELPER
// ============================================

const getElementIcon = (type: ElementType, size: number = 20) => {
  switch (type) {
    case 'screen':
      return <Monitor size={size} className="text-blue-500" />;
    case 'decision':
      return <GitBranch size={size} className="text-yellow-500" />;
    case 'assignment':
      return <Equal size={size} className="text-purple-500" />;
    case 'loop':
      return <RefreshCw size={size} className="text-orange-500" />;
    case 'get_records':
      return <Download size={size} className="text-cyan-500" />;
    case 'create_records':
      return <PlusCircle size={size} className="text-green-500" />;
    case 'update_records':
      return <Edit2 size={size} className="text-blue-500" />;
    case 'delete_records':
      return <Trash2 size={size} className="text-red-500" />;
    case 'email_alert':
      return <Mail size={size} className="text-pink-500" />;
    case 'post_to_feed':
      return <MessageSquare size={size} className="text-indigo-500" />;
    case 'wait':
      return <Clock size={size} className="text-gray-500" />;
    case 'action':
      return <Zap size={size} className="text-yellow-500" />;
    case 'subflow':
      return <Database size={size} className="text-teal-500" />;
    default:
      return <Zap size={size} className="text-gray-500" />;
  }
};

// ============================================
// SCREEN CONFIG FORM
// ============================================

interface ScreenConfigFormProps {
  config: ScreenConfig;
  onChange: (config: ScreenConfig) => void;
}

const ScreenConfigForm: React.FC<ScreenConfigFormProps> = ({ config, onChange }) => {
  const addField = () => {
    const newField: ScreenField = {
      id: `field_${Date.now()}`,
      type: 'input',
      fieldType: 'text',
      label: '',
      apiName: '',
      required: false
    };
    onChange({
      ...config,
      fields: [...config.fields, newField]
    });
  };

  const updateField = (index: number, updates: Partial<ScreenField>) => {
    const newFields = [...config.fields];
    newFields[index] = { ...newFields[index], ...updates };
    onChange({ ...config, fields: newFields });
  };

  const removeField = (index: number) => {
    onChange({
      ...config,
      fields: config.fields.filter((_, i) => i !== index)
    });
  };

  return (
    <div className="space-y-6">
      {/* Screen Options */}
      <div className="grid grid-cols-2 gap-4">
        <StandardCheckbox
          label="Show Header"
          checked={config.showHeader}
          onChange={(checked) => onChange({ ...config, showHeader: checked })}
        />
        <StandardCheckbox
          label="Show Footer"
          checked={config.showFooter}
          onChange={(checked) => onChange({ ...config, showFooter: checked })}
        />
        <StandardCheckbox
          label="Allow Back"
          checked={config.allowBack}
          onChange={(checked) => onChange({ ...config, allowBack: checked })}
        />
        <StandardCheckbox
          label="Allow Pause"
          checked={config.allowPause}
          onChange={(checked) => onChange({ ...config, allowPause: checked })}
        />
      </div>

      {config.showHeader && (
        <StandardInput
          label="Header Text"
          value={config.headerText || ''}
          onChange={(e) => onChange({ ...config, headerText: e.target.value })}
          placeholder="Enter header text..."
        />
      )}

      {/* Screen Fields */}
      <div>
        <div className="flex items-center justify-between mb-3">
          <h4 className="font-medium text-gray-900 dark:text-gray-100">
            Screen Fields
          </h4>
          <StandardButton
            variant="secondary"
            size="sm"
            onClick={addField}
            leftIcon={<Plus size={14} />}
          >
            Add Field
          </StandardButton>
        </div>

        {config.fields.length === 0 ? (
          <div className="text-center py-6 bg-gray-50 dark:bg-gray-800 rounded-lg">
            <p className="text-gray-500 dark:text-gray-400 text-sm">
              No fields added yet. Add fields to create your screen.
            </p>
          </div>
        ) : (
          <div className="space-y-3">
            {config.fields.map((field, index) => (
              <StandardCard key={field.id} className="p-4">
                <div className="flex items-start gap-3">
                  <div className="flex-1 grid grid-cols-2 gap-3">
                    <StandardInput
                      label="Label"
                      value={field.label}
                      onChange={(e) => updateField(index, { label: e.target.value })}
                      placeholder="Field label"
                    />
                    <StandardSelect
                      label="Type"
                      value={field.fieldType}
                      onChange={(e) => updateField(index, { fieldType: e.target.value })}
                      options={FIELD_TYPES}
                    />
                    <StandardInput
                      label="API Name"
                      value={field.apiName}
                      onChange={(e) => updateField(index, { apiName: e.target.value })}
                      placeholder="field_api_name"
                    />
                    <div className="flex items-center pt-6">
                      <StandardCheckbox
                        label="Required"
                        checked={field.required}
                        onChange={(checked) => updateField(index, { required: checked })}
                      />
                    </div>
                  </div>
                  <button
                    onClick={() => removeField(index)}
                    className="p-2 text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 rounded"
                  >
                    <X size={16} />
                  </button>
                </div>
              </StandardCard>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

// ============================================
// DECISION CONFIG FORM
// ============================================

interface DecisionConfigFormProps {
  config: DecisionConfig;
  variables: FlowVariable[];
  onChange: (config: DecisionConfig) => void;
}

const DecisionConfigForm: React.FC<DecisionConfigFormProps> = ({
  config,
  variables,
  onChange
}) => {
  const addOutcome = () => {
    const newOutcome: DecisionOutcome = {
      id: `outcome_${Date.now()}`,
      name: `Outcome ${config.outcomes.length + 1}`,
      label: `Outcome ${config.outcomes.length + 1}`,
      conditionLogic: 'AND',
      conditions: [],
      connectorId: '',
      order: config.outcomes.length
    };
    onChange({
      ...config,
      outcomes: [...config.outcomes, newOutcome]
    });
  };

  const updateOutcome = (index: number, updates: Partial<DecisionOutcome>) => {
    const newOutcomes = [...config.outcomes];
    newOutcomes[index] = { ...newOutcomes[index], ...updates };
    onChange({ ...config, outcomes: newOutcomes });
  };

  const removeOutcome = (index: number) => {
    onChange({
      ...config,
      outcomes: config.outcomes.filter((_, i) => i !== index)
    });
  };

  const addCondition = (outcomeIndex: number) => {
    const newCondition: FlowCondition = {
      id: `cond_${Date.now()}`,
      leftValue: '',
      operator: 'equals',
      rightValue: '',
      rightValueType: 'literal'
    };
    const newOutcomes = [...config.outcomes];
    newOutcomes[outcomeIndex].conditions.push(newCondition);
    onChange({ ...config, outcomes: newOutcomes });
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h4 className="font-medium text-gray-900 dark:text-gray-100">
          Outcomes
        </h4>
        <StandardButton
          variant="secondary"
          size="sm"
          onClick={addOutcome}
          leftIcon={<Plus size={14} />}
        >
          Add Outcome
        </StandardButton>
      </div>

      {config.outcomes.map((outcome, index) => (
        <StandardCard key={outcome.id} className="p-4">
          <div className="flex items-start justify-between mb-4">
            <StandardInput
              label="Outcome Name"
              value={outcome.label}
              onChange={(e) => updateOutcome(index, { label: e.target.value, name: e.target.value })}
              placeholder="e.g., High Value Customer"
            />
            <button
              onClick={() => removeOutcome(index)}
              className="p-2 text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 rounded ml-2"
            >
              <X size={16} />
            </button>
          </div>

          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <span className="text-sm text-gray-600 dark:text-gray-400">Conditions</span>
              <StandardButton
                variant="ghost"
                size="sm"
                onClick={() => addCondition(index)}
              >
                <Plus size={14} />
              </StandardButton>
            </div>
            {outcome.conditions.length === 0 ? (
              <p className="text-xs text-gray-500 dark:text-gray-400 py-2">
                No conditions - this outcome will always be true
              </p>
            ) : (
              outcome.conditions.map((cond, condIndex) => (
                <div key={cond.id} className="flex items-center gap-2 text-sm">
                  <StandardSelect
                    value={cond.leftValue}
                    onChange={(e) => {
                      const newOutcomes = [...config.outcomes];
                      newOutcomes[index].conditions[condIndex].leftValue = e.target.value;
                      onChange({ ...config, outcomes: newOutcomes });
                    }}
                    options={[
                      { value: '', label: 'Select field' },
                      ...variables.map(v => ({ value: v.apiName, label: v.name }))
                    ]}
                    className="flex-1"
                  />
                  <StandardSelect
                    value={cond.operator}
                    onChange={(e) => {
                      const newOutcomes = [...config.outcomes];
                      newOutcomes[index].conditions[condIndex].operator = e.target.value as FlowCondition['operator'];
                      onChange({ ...config, outcomes: newOutcomes });
                    }}
                    options={OPERATORS}
                    className="w-40"
                  />
                  <StandardInput
                    value={cond.rightValue}
                    onChange={(e) => {
                      const newOutcomes = [...config.outcomes];
                      newOutcomes[index].conditions[condIndex].rightValue = e.target.value;
                      onChange({ ...config, outcomes: newOutcomes });
                    }}
                    placeholder="Value"
                    className="flex-1"
                  />
                </div>
              ))
            )}
          </div>
        </StandardCard>
      ))}

      {/* Default Outcome */}
      <div className="p-4 bg-gray-50 dark:bg-gray-800 rounded-lg">
        <p className="text-sm text-gray-600 dark:text-gray-400">
          <strong>Default Outcome:</strong> If no conditions match, the flow will follow the default path.
        </p>
      </div>
    </div>
  );
};

// ============================================
// ASSIGNMENT CONFIG FORM
// ============================================

interface AssignmentConfigFormProps {
  config: AssignmentConfig;
  variables: FlowVariable[];
  onChange: (config: AssignmentConfig) => void;
}

const AssignmentConfigForm: React.FC<AssignmentConfigFormProps> = ({
  config,
  variables,
  onChange
}) => {
  const addAssignment = () => {
    const newAssignment: AssignmentItem = {
      id: `assign_${Date.now()}`,
      targetVariable: '',
      operator: 'equals',
      value: '',
      valueType: 'literal'
    };
    onChange({
      ...config,
      assignments: [...config.assignments, newAssignment]
    });
  };

  const updateAssignment = (index: number, updates: Partial<AssignmentItem>) => {
    const newAssignments = [...config.assignments];
    newAssignments[index] = { ...newAssignments[index], ...updates };
    onChange({ ...config, assignments: newAssignments });
  };

  const removeAssignment = (index: number) => {
    onChange({
      ...config,
      assignments: config.assignments.filter((_, i) => i !== index)
    });
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h4 className="font-medium text-gray-900 dark:text-gray-100">
          Variable Assignments
        </h4>
        <StandardButton
          variant="secondary"
          size="sm"
          onClick={addAssignment}
          leftIcon={<Plus size={14} />}
        >
          Add Assignment
        </StandardButton>
      </div>

      {config.assignments.length === 0 ? (
        <div className="text-center py-6 bg-gray-50 dark:bg-gray-800 rounded-lg">
          <p className="text-gray-500 dark:text-gray-400 text-sm">
            No assignments added yet.
          </p>
        </div>
      ) : (
        <div className="space-y-3">
          {config.assignments.map((assignment, index) => (
            <div key={assignment.id} className="flex items-center gap-2">
              <StandardSelect
                value={assignment.targetVariable}
                onChange={(e) => updateAssignment(index, { targetVariable: e.target.value })}
                options={[
                  { value: '', label: 'Select variable' },
                  ...variables.map(v => ({ value: v.apiName, label: v.name }))
                ]}
                className="flex-1"
              />
              <span className="text-gray-500">=</span>
              <StandardSelect
                value={assignment.valueType}
                onChange={(e) => updateAssignment(index, { valueType: e.target.value as AssignmentItem['valueType'] })}
                options={[
                  { value: 'literal', label: 'Value' },
                  { value: 'variable', label: 'Variable' },
                  { value: 'formula', label: 'Formula' }
                ]}
                className="w-28"
              />
              <StandardInput
                value={assignment.value}
                onChange={(e) => updateAssignment(index, { value: e.target.value })}
                placeholder="Enter value..."
                className="flex-1"
              />
              <button
                onClick={() => removeAssignment(index)}
                className="p-2 text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 rounded"
              >
                <X size={16} />
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// ============================================
// GET RECORDS CONFIG FORM
// ============================================

interface GetRecordsConfigFormProps {
  config: GetRecordsConfig;
  variables: FlowVariable[];
  onChange: (config: GetRecordsConfig) => void;
}

const GetRecordsConfigForm: React.FC<GetRecordsConfigFormProps> = ({
  config,
  variables,
  onChange
}) => {
  return (
    <div className="space-y-4">
      <StandardSelect
        label="Object"
        value={config.objectType}
        onChange={(e) => onChange({ ...config, objectType: e.target.value })}
        options={[{ value: '', label: 'Select Object' }, ...OBJECT_TYPES]}
        required
      />

      <StandardSelect
        label="Store Result In"
        value={config.outputVariable}
        onChange={(e) => onChange({ ...config, outputVariable: e.target.value })}
        options={[
          { value: '', label: 'Select variable' },
          ...variables.filter(v => v.type === 'record' || v.type === 'collection')
            .map(v => ({ value: v.apiName, label: v.name }))
        ]}
      />

      <StandardSelect
        label="How Many Records"
        value={config.storeAs}
        onChange={(e) => onChange({ ...config, storeAs: e.target.value as 'first_record' | 'all_records' })}
        options={[
          { value: 'first_record', label: 'Only the first record' },
          { value: 'all_records', label: 'All records' }
        ]}
      />

      <div className="grid grid-cols-2 gap-4">
        <StandardInput
          label="Sort By"
          value={config.sortField || ''}
          onChange={(e) => onChange({ ...config, sortField: e.target.value })}
          placeholder="Field API name"
        />
        <StandardSelect
          label="Sort Direction"
          value={config.sortDirection || 'ASC'}
          onChange={(e) => onChange({ ...config, sortDirection: e.target.value as 'ASC' | 'DESC' })}
          options={[
            { value: 'ASC', label: 'Ascending' },
            { value: 'DESC', label: 'Descending' }
          ]}
        />
      </div>

      <StandardInput
        label="Limit"
        type="number"
        value={config.limit?.toString() || ''}
        onChange={(e) => onChange({ ...config, limit: parseInt(e.target.value) || undefined })}
        placeholder="Max records to return"
      />

      <StandardCheckbox
        label="If no records found, set variable to null"
        checked={config.nullIfNoRecords}
        onChange={(checked) => onChange({ ...config, nullIfNoRecords: checked })}
      />
    </div>
  );
};

// ============================================
// EMAIL ALERT CONFIG FORM
// ============================================

interface EmailAlertConfigFormProps {
  config: EmailAlertConfig;
  onChange: (config: EmailAlertConfig) => void;
}

const EmailAlertConfigForm: React.FC<EmailAlertConfigFormProps> = ({
  config,
  onChange
}) => {
  return (
    <div className="space-y-4">
      <StandardCheckbox
        label="Use Email Template"
        checked={config.useTemplate}
        onChange={(checked) => onChange({ ...config, useTemplate: checked })}
      />

      {config.useTemplate ? (
        <StandardInput
          label="Template Name"
          value={config.templateName || ''}
          onChange={(e) => onChange({ ...config, templateName: e.target.value })}
          placeholder="Select or enter template name"
        />
      ) : (
        <>
          <StandardInput
            label="Subject"
            value={config.subject || ''}
            onChange={(e) => onChange({ ...config, subject: e.target.value })}
            placeholder="Email subject"
          />
          <StandardTextarea
            label="Body"
            value={config.body || ''}
            onChange={(e) => onChange({ ...config, body: e.target.value })}
            placeholder="Email body (supports merge fields)"
            rows={6}
          />
        </>
      )}

      <div>
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
          Recipients
        </label>
        <p className="text-xs text-gray-500 dark:text-gray-400 mb-2">
          Add email addresses separated by commas
        </p>
        <StandardInput
          value={config.recipients?.map(r => r.value).join(', ') || ''}
          onChange={(e) => {
            const emails = e.target.value.split(',').map(email => ({
              type: 'email' as const,
              value: email.trim()
            })).filter(r => r.value);
            onChange({ ...config, recipients: emails });
          }}
          placeholder="email@example.com, another@example.com"
        />
      </div>
    </div>
  );
};

// ============================================
// WAIT CONFIG FORM
// ============================================

interface WaitConfigFormProps {
  config: WaitConfig;
  onChange: (config: WaitConfig) => void;
}

const WaitConfigForm: React.FC<WaitConfigFormProps> = ({
  config,
  onChange
}) => {
  return (
    <div className="space-y-4">
      <StandardSelect
        label="Wait Type"
        value={config.waitType}
        onChange={(e) => onChange({ ...config, waitType: e.target.value as WaitConfig['waitType'] })}
        options={[
          { value: 'relative_time', label: 'Wait for a specified time' },
          { value: 'absolute_time', label: 'Wait until a specific date/time' },
          { value: 'platform_event', label: 'Wait for a platform event' }
        ]}
      />

      {config.waitType === 'relative_time' && (
        <div className="grid grid-cols-2 gap-4">
          <StandardInput
            label="Duration"
            type="number"
            value={config.offsetNumber?.toString() || ''}
            onChange={(e) => onChange({ ...config, offsetNumber: parseInt(e.target.value) || 0 })}
            placeholder="Enter number"
          />
          <StandardSelect
            label="Unit"
            value={config.offsetUnit || 'hours'}
            onChange={(e) => onChange({ ...config, offsetUnit: e.target.value as WaitConfig['offsetUnit'] })}
            options={[
              { value: 'minutes', label: 'Minutes' },
              { value: 'hours', label: 'Hours' },
              { value: 'days', label: 'Days' }
            ]}
          />
        </div>
      )}

      {config.waitType === 'absolute_time' && (
        <StandardInput
          label="Wait Until"
          type="datetime-local"
          value={config.absoluteDateTime || ''}
          onChange={(e) => onChange({ ...config, absoluteDateTime: e.target.value })}
        />
      )}

      {config.waitType === 'platform_event' && (
        <StandardInput
          label="Event Type"
          value={config.eventType || ''}
          onChange={(e) => onChange({ ...config, eventType: e.target.value })}
          placeholder="Enter platform event API name"
        />
      )}

      <StandardInput
        label="Timeout (hours)"
        type="number"
        value={config.timeoutHours?.toString() || ''}
        onChange={(e) => onChange({ ...config, timeoutHours: parseInt(e.target.value) || undefined })}
        placeholder="Optional timeout"
        helpText="If set, the flow will continue after this time if the wait condition is not met"
      />
    </div>
  );
};

// ============================================
// MAIN COMPONENT
// ============================================

export const ElementConfigModal: React.FC<ElementConfigModalProps> = ({
  isOpen,
  onClose,
  element,
  variables,
  onSave
}) => {
  const { addToast } = useToast();
  const [editedElement, setEditedElement] = useState<FlowElement | null>(null);

  useEffect(() => {
    if (element) {
      setEditedElement({ ...element });
    }
  }, [element]);

  if (!editedElement) return null;

  const handleSave = () => {
    if (!editedElement.label.trim()) {
      addToast({
        type: 'error',
        title: 'Validation Error',
        message: 'Element label is required'
      });
      return;
    }

    onSave(editedElement);
    onClose();
  };

  const updateConfig = (config: typeof editedElement.config) => {
    setEditedElement({ ...editedElement, config });
  };

  const renderConfigForm = () => {
    switch (editedElement.type) {
      case 'screen':
        return (
          <ScreenConfigForm
            config={editedElement.config as ScreenConfig}
            onChange={updateConfig}
          />
        );
      case 'decision':
        return (
          <DecisionConfigForm
            config={editedElement.config as DecisionConfig}
            variables={variables}
            onChange={updateConfig}
          />
        );
      case 'assignment':
        return (
          <AssignmentConfigForm
            config={editedElement.config as AssignmentConfig}
            variables={variables}
            onChange={updateConfig}
          />
        );
      case 'get_records':
        return (
          <GetRecordsConfigForm
            config={editedElement.config as GetRecordsConfig}
            variables={variables}
            onChange={updateConfig}
          />
        );
      case 'email_alert':
        return (
          <EmailAlertConfigForm
            config={editedElement.config as EmailAlertConfig}
            onChange={updateConfig}
          />
        );
      case 'wait':
        return (
          <WaitConfigForm
            config={editedElement.config as WaitConfig}
            onChange={updateConfig}
          />
        );
      default:
        return (
          <div className="p-4 bg-gray-50 dark:bg-gray-800 rounded-lg">
            <p className="text-gray-600 dark:text-gray-400">
              Configuration for {editedElement.type.replace(/_/g, ' ')} elements is not yet available.
            </p>
          </div>
        );
    }
  };

  return (
    <StandardModal
      isOpen={isOpen}
      onClose={onClose}
      title={`Configure ${editedElement.type.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}`}
      size="lg"
    >
      <div className="p-6 space-y-6">
        {/* Basic Info */}
        <div className="grid grid-cols-2 gap-4">
          <StandardInput
            label="Label"
            value={editedElement.label}
            onChange={(e) => setEditedElement({ ...editedElement, label: e.target.value })}
            placeholder="Enter element label"
            required
          />
          <StandardInput
            label="API Name"
            value={editedElement.name}
            onChange={(e) => setEditedElement({ ...editedElement, name: e.target.value })}
            placeholder="element_api_name"
          />
        </div>

        <StandardTextarea
          label="Description"
          value={editedElement.description || ''}
          onChange={(e) => setEditedElement({ ...editedElement, description: e.target.value })}
          placeholder="Optional description of what this element does"
          rows={2}
        />

        <hr className="border-gray-200 dark:border-gray-700" />

        {/* Element-specific config */}
        {renderConfigForm()}
      </div>

      {/* Footer */}
      <div className="p-4 border-t border-gray-200 dark:border-gray-700 flex justify-end gap-3">
        <StandardButton variant="secondary" onClick={onClose}>
          Cancel
        </StandardButton>
        <StandardButton variant="primary" onClick={handleSave}>
          Save Changes
        </StandardButton>
      </div>
    </StandardModal>
  );
};

export default ElementConfigModal;
